import type { BaseIssue, ErrorMessage } from "valibot";
import { instance, nonOptional, pipe, string, transform, union, check } from "valibot";
import FieldId from "../../fields/FieldId";
import { errorTranslation } from "./base";
import SubformId from "../../state/SubformId";
import CustomerId from "../../state/CustomerId";
import FolderId from "../../state/FolderId";
import FormId from "../../state/FormId";
import type Id from "../../types/Id";
import { isString } from "lodash-es";
import TemplateId from "../../state/TemplateId";
import FormVersionId from "../../state/FormVersionId";
import GroupId from "../../state/GroupId";
import RoleId from "../../state/RoleId";
import UserId from "../../state/UserId";
import ServiceAccountId from "../../state/ServiceAccountId";
import { keyFromSelector } from "i18next";

let subformExists: (id: SubformId) => boolean = () => true;

// Easy fix for circular import problems with the SubformService
// Should be fixed with DEV-8463
export const registerSubformExistsCheck = (fn: (id: SubformId) => boolean): void => {
  subformExists = fn;
};

export const fieldReference = (message?: ErrorMessage<BaseIssue<unknown>>) =>
  idReference<FieldId>(FieldId, message ?? errorTranslation(keyFromSelector(($) => $.VALIDATION_NO_SELECTION))); // Accept string as valid id as long as it can be transformed to the given id class.

export const subformReference = (message?: ErrorMessage<BaseIssue<unknown>>) =>
  pipe(
    idReference<SubformId>(SubformId, message ?? errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY))),
    check((id) => subformExists(id), errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY))),
  );

export const templateReference = (message?: ErrorMessage<BaseIssue<unknown>>) =>
  idReference<TemplateId>(TemplateId, message ?? errorTranslation(keyFromSelector(($) => $.VALIDATION_VALID_TEMPLATE)));

export const formReference = (message?: ErrorMessage<BaseIssue<unknown>>) =>
  idReference<FormId>(FormId, message ?? errorTranslation(keyFromSelector(($) => $.VALIDATION_VALID_FORM)));

export const formVersionReference = (message?: ErrorMessage<BaseIssue<unknown>>) =>
  idReference<FormVersionId>(
    FormVersionId,
    message ?? errorTranslation(keyFromSelector(($) => $.VALIDATION_VALID_FORM_VERSION)),
  );

export const folderReference = (message?: ErrorMessage<BaseIssue<unknown>>) =>
  idReference<FolderId>(FolderId, message ?? errorTranslation(keyFromSelector(($) => $.VALIDATION_VALID_FOLDER)));

export const customerReference = (message?: ErrorMessage<BaseIssue<unknown>>) =>
  idReference<CustomerId>(CustomerId, message ?? errorTranslation(keyFromSelector(($) => $.VALIDATION_VALID_CUSTOMER)));

export const userReference = (message?: ErrorMessage<BaseIssue<unknown>>) =>
  idReference<UserId>(UserId, message ?? errorTranslation(keyFromSelector(($) => $.VALIDATION_VALID_USER)));

export const groupReference = (message?: ErrorMessage<BaseIssue<unknown>>) =>
  idReference<GroupId>(GroupId, message ?? errorTranslation(keyFromSelector(($) => $.VALIDATION_VALID_GROUP)));

export const roleReference = (message?: ErrorMessage<BaseIssue<unknown>>) =>
  idReference<RoleId>(RoleId, message ?? errorTranslation(keyFromSelector(($) => $.VALIDATION_VALID_ROLE)));

export const serviceAccountReference = (message?: ErrorMessage<BaseIssue<unknown>>) =>
  idReference<ServiceAccountId>(
    ServiceAccountId,
    message ?? errorTranslation(keyFromSelector(($) => $.VALIDATION_SERVICE_ACCOUNT)),
  );

const idReference = <T extends Id<string>>(
  idClass: { new (value: string): T; from(value: string): T },
  message: ErrorMessage<BaseIssue<unknown>>,
) =>
  nonOptional(
    pipe(
      union([instance(idClass), string()], message),
      transform((value) => (isString(value) ? idClass.from(value) : value)),
      instance(idClass, message),
    ),
    message,
  );
